import React from 'react';

import ScrollReveal from './ScrollReveal';

const DURATION = 1800;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Counts up from 0 to `value` the first time it scrolls into view.
 * Falls back to the final number immediately when the visitor has asked
 * for reduced motion, or when IntersectionObserver is unavailable.
 */
function CountUp({ value, prefix = '', suffix = '' }) {
  const [display, setDisplay] = React.useState(0);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const node = ref.current;
    let frame = null;
    let observer = null;
    // Scoped to this effect, not a ref: StrictMode's dev remount tears the
    // effect down and re-runs it, and a ref guard would survive that teardown
    // and block the restart, leaving the counter stuck on 0.
    let started = false;

    const run = () => {
      if (started) return;
      started = true;

      if (prefersReducedMotion()) {
        setDisplay(value);
        return;
      }

      const start = performance.now();

      const tick = (now) => {
        const progress = Math.min((now - start) / DURATION, 1);
        // easeOutCubic — fast at first, settles gently on the final number
        const eased = 1 - Math.pow(1 - progress, 3);
        setDisplay(Math.round(value * eased));
        if (progress < 1) frame = requestAnimationFrame(tick);
      };

      frame = requestAnimationFrame(tick);
    };

    // Already on screen at mount (deep link, restored scroll position, or a
    // short page)? Start straight away rather than waiting on the observer.
    const inView = () => {
      if (!node) return false;
      const rect = node.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom > 0;
    };

    if (!node || typeof IntersectionObserver === 'undefined' || inView()) {
      run();
    } else {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              run();
              if (observer) observer.disconnect();
            }
          });
        },
        { threshold: 0.3 }
      );
      observer.observe(node);
    }

    return () => {
      if (observer) observer.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [value]);

  return (
    <span ref={ref}>
      {prefix}
      {display.toLocaleString('en-US')}
      {suffix}
    </span>
  );
}

/**
 * Floating impact-stats card — same styling as the one on the About page,
 * with the numbers counting up as they scroll into view.
 */
// Full class strings so Tailwind's scanner picks them up.
const COLUMNS = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-2 md:grid-cols-3',
  4: 'grid-cols-2 md:grid-cols-4',
};

function ImpactStats({ stats, className = '' }) {
  const columns = COLUMNS[stats.length] || COLUMNS[4];

  return (
    <section className={`relative z-20 -mt-14 sm:-mt-16 pb-4 ${className}`}>
      <div className="container-custom">
        <ScrollReveal>
          <div className="max-w-5xl mx-auto bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-6 sm:p-8 border border-white/40">
            <div className={`grid ${columns} gap-4 sm:gap-6`}>
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#F4900C] mb-1">
                    <CountUp
                      value={stat.value}
                      prefix={stat.prefix}
                      suffix={stat.suffix}
                    />
                  </div>
                  <div className="text-gray-600 font-medium text-xs sm:text-sm md:text-base">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

export default ImpactStats;
